import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  Building,
  User,
  MapPin,
  Mail,
  Loader2,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { API_ENDPOINTS } from "../../lib/config";
import { AuthService, fetchWithAuth } from "../../lib/authService";

const STEP_TITLES = [
  "Partner Profile",
  "Location & Details",
];

export default function BusinessSignup() {
  const navigate = useNavigate();

  // Step state: 0 = Profile & Role, 1 = Location & Personal details
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1: Roles, Name, Stage Name, Avatar
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [fullName, setFullName] = useState("");
  const [stageName, setStageName] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Step 2: Location & Personal Details
  const [locationAddress, setLocationAddress] = useState("");
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("Male");
  const [dob, setDob] = useState({ day: "01", month: "01", year: "2000" });
  const [referralCode, setReferralCode] = useState(
    localStorage.getItem("referralCode") || ""
  );

  // Ensure authenticated
  useEffect(() => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      toast.error("Please login to complete business registration");
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Fetch Performer Roles
  useEffect(() => {
    let isMounted = true;
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await fetch(API_ENDPOINTS.PERFORMER_ROLES);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            if (isMounted) {
              setRoles(data);
              setSelectedRole(data[0]);
            }
            return;
          }
        }
        throw new Error("Unable to fetch roles");
      } catch (err) {
        console.error("Error fetching roles, using fallback:", err);
        if (isMounted) {
          const fallbackRoles = [
            { id: "69c106fd1cc204a317dc9d4b", name: "dj", displayName: "DJ", emoji: "🎧", description: "DJ Sets & Mixes" },
            { id: "69c1126f1cc204a317dc9df0", name: "live_singer", displayName: "Live Singer", emoji: "🎤", description: "Vocalists & Singers" },
            { id: "69c1128f1cc204a317dc9df5", name: "live_band", displayName: "Live Band", emoji: "🥁", description: "Live Bands & Orchestras" },
            { id: "69c112af1cc204a317dc9dfa", name: "anchor", displayName: "Anchor", emoji: "🎙️", description: "Emcees & Event Hosts" },
          ];
          setRoles(fallbackRoles);
          setSelectedRole(fallbackRoles[0]);
        }
      } finally {
        if (isMounted) {
          setLoadingRoles(false);
        }
      }
    };
    fetchRoles();
    return () => {
      isMounted = false;
    };
  }, []);

  // Formatted display name helper (matches Android MinimalSignupStep1Fragment)
  const formattedStageName = useMemo(() => {
    const rolePrefix = selectedRole?.displayName || selectedRole?.name?.toUpperCase() || "DJ";
    const cleaned = stageName.replace(new RegExp(`^${rolePrefix}\\s*`, "i"), "").trim();
    if (!cleaned) return rolePrefix;
    return `${rolePrefix} ${cleaned}`;
  }, [selectedRole, stageName]);

  // Year options for DOB (up to 16 years ago matching Android)
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 16;
  const years = useMemo(() => {
    const arr = [];
    for (let y = maxYear; y >= 1950; y--) arr.push(y);
    return arr;
  }, [maxYear]);

  const days = useMemo(() => {
    return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
  }, []);

  const months = useMemo(() => [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ], []);

  // Avatar upload handler
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      toast.error("Please upload a JPG or PNG image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setProfileImage(URL.createObjectURL(file));
    setUploadingAvatar(true);

    const token = localStorage.getItem("tringAuthToken");
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const res = await fetch(API_ENDPOINTS.PARTNER_UPLOAD_AVATAR, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to upload avatar");
      }

      const json = await res.json();
      setAvatarUrl(json.avatarUrl || json.url || "");
      toast.success("Profile photo uploaded!");
    } catch (err) {
      console.error("Avatar upload failed:", err);
      toast.error(err.message || "Could not upload photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Google Places select handler
  const handleSelectLocation = async (address) => {
    setLocationAddress(address);
    try {
      const results = await geocodeByAddress(address);
      if (results && results[0]) {
        const ac = results[0].address_components || [];
        let extractedCity = "";
        let extractedArea = "";
        let extractedState = "";

        for (const c of ac) {
          if (c.types.includes("locality")) extractedCity = c.long_name;
          else if (!extractedCity && c.types.includes("administrative_area_level_2")) {
            extractedCity = c.long_name;
          }
          if (c.types.includes("sublocality") || c.types.includes("neighborhood")) {
            extractedArea = c.long_name;
          }
          if (c.types.includes("administrative_area_level_1")) {
            extractedState = c.long_name;
          }
        }
        setCity(extractedCity || address.split(",")[0]?.trim() || "");
        setArea(extractedArea || "");
        setState(extractedState || "");
      }
    } catch (err) {
      console.error("Geocoding error:", err);
      setCity(address.split(",")[0]?.trim() || "");
    }
  };

  // Validation
  const validateStep1 = () => {
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return false;
    }
    if (!stageName.trim()) {
      toast.error("Please enter your stage/display name");
      return false;
    }
    if (!selectedRole) {
      toast.error("Please select a role");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!locationAddress.trim()) {
      toast.error("Please enter your location");
      return false;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    return true;
  };

  // Final submission matching Android MinimalSignUpRequest
  const handleCompleteRegistration = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      navigate("/");
      return;
    }

    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    const rawRoleId = selectedRole?._id || selectedRole?.id || null;
    const isObjectId = typeof rawRoleId === "string" && /^[0-9a-fA-F]{24}$/.test(rawRoleId);
    const validRoleId = isObjectId ? rawRoleId : null;

    const dobString = `${dob.year}-${dob.month.toString().padStart(2, "0")}-${dob.day.toString().padStart(2, "0")}`;

    const payload = {
      first_name: firstName,
      last_name: lastName,
      display_name: formattedStageName,
      roleId: validRoleId,
      category: selectedRole?.name || "dj",
      dob: dobString,
      gender: gender,
      email: email.trim(),
      phone_number: localStorage.getItem("number") || "",
      location: locationAddress.trim(),
      city: city.trim() || locationAddress.trim(),
      locality: area.trim() || "",
      state: state.trim() || "",
      referredBy: referralCode.trim() || null,
      avatar: avatarUrl || null,
      isBusinessUser: true,
      isLive: false,
    };

    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PARTNER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Registration failed");
      }

      localStorage.setItem("isBusiness", "true");
      localStorage.setItem("isUserRegistered", "true");
      AuthService.setUserRegistered(true);
      toast.success("Welcome to Tringbox Business!");
      navigate("/loggedinartistbussiness", { replace: true });
    } catch (err) {
      console.error("Business registration error:", err);
      toast.error(err.message || "An error occurred during registration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      AuthService.clearTokens();
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {}
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAF9FD] flex flex-col text-gray-800">
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Top Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-2xs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                  {STEP_TITLES[currentStep]}
                </h1>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-[#6720A4] uppercase tracking-wide">
                  <Building className="w-3 h-3" /> Business Partner
                </span>
              </div>
              <p className="text-[11px] text-gray-400 hidden sm:block">
                Step {currentStep + 1} of 2 • Fast-track Onboarding
              </p>
            </div>
          </div>

          {/* Stepper Pills */}
          <div className="flex items-center gap-2">
            {STEP_TITLES.map((title, idx) => {
              const isPast = idx < currentStep;
              const isCurrent = idx === currentStep;
              return (
                <div key={title} className="flex items-center gap-1.5">
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                      isCurrent
                        ? "bg-[#6720A4] text-white shadow-xs"
                        : isPast
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <span>{isPast ? "✓" : idx + 1}</span>
                    <span className="hidden sm:inline">{title}</span>
                  </div>
                  {idx < STEP_TITLES.length - 1 && (
                    <div className="w-2.5 h-0.5 bg-gray-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Form Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <AnimatePresence mode="wait">
          {/* STEP 1: Profession, Name, Stage Name & Avatar */}
          {currentStep === 0 && (
            <motion.div
              key="step-profile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="text-center space-y-1">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Set Up Your Business Profile
                </h2>
                <p className="text-sm text-gray-500 font-medium">
                  Add your performer role and stage name for your venue events
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-150 space-y-6">
                {/* Avatar Upload */}
                <div className="flex flex-col items-center justify-center space-y-2">
                  <div className="relative group cursor-pointer">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden bg-gradient-to-tr from-purple-100 to-indigo-50 border-2 border-purple-200 shadow-inner flex items-center justify-center relative">
                      {profileImage || avatarUrl ? (
                        <img
                          src={profileImage || avatarUrl}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-10 h-10 text-purple-400" />
                      )}
                      {uploadingAvatar && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    <label
                      htmlFor="avatar-input"
                      className="absolute bottom-0 right-0 p-2 rounded-full bg-[#6720A4] text-white shadow-md hover:bg-[#551887] cursor-pointer transition-transform group-hover:scale-105"
                      aria-label="Upload photo"
                    >
                      <Upload className="w-4 h-4" />
                      <input
                        id="avatar-input"
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <span className="text-xs font-semibold text-purple-700">
                    Add Profile Photo
                  </span>
                </div>

                {/* Profession / Role Selection (DJ, Singer, Live Band, etc.) */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                    Select Your Profession / Role *
                  </label>
                  {loadingRoles ? (
                    <div className="py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin text-[#6720A4]" />
                      <span>Loading roles...</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {roles.map((r) => {
                        const isSelected = selectedRole?.name === r.name;
                        const roleEmoji =
                          r.emoji ||
                          (r.name.toLowerCase().includes("dj")
                            ? "🎧"
                            : r.name.toLowerCase().includes("singer")
                            ? "🎤"
                            : r.name.toLowerCase().includes("band")
                            ? "🥁"
                            : "🎙️");
                        return (
                          <button
                            key={r.id || r._id || r.name}
                            type="button"
                            onClick={() => setSelectedRole(r)}
                            className={`p-3.5 rounded-2xl border-2 flex flex-col items-center justify-center text-center transition-all ${
                              isSelected
                                ? "border-[#6720A4] bg-purple-50/70 text-[#6720A4] font-bold shadow-xs scale-[1.02]"
                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            <span className="text-2xl mb-1">{roleEmoji}</span>
                            <span className="text-xs sm:text-sm font-bold">
                              {r.displayName || r.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Full Name & Stage Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                      Full Legal Name *
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="e.g. Aditya Sharma"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                      Stage / Brand Name *
                    </label>
                    <input
                      type="text"
                      value={stageName}
                      onChange={(e) => setStageName(e.target.value)}
                      placeholder="e.g. Dev or King"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors"
                    />
                  </div>
                </div>

                {/* Live Preview Banner matching Android */}
                <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border border-purple-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Sparkles className="w-5 h-5 text-[#6720A4]" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-400">
                        Visible to Audience As
                      </p>
                      <p className="text-sm sm:text-base font-extrabold text-[#6720A4]">
                        {formattedStageName}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white text-purple-700 border border-purple-150 shadow-2xs">
                    Live Preview
                  </span>
                </div>
              </div>

              {/* Next Button */}
              <button
                type="button"
                onClick={() => {
                  if (validateStep1()) setCurrentStep(1);
                }}
                className="w-full bg-[#6720A4] hover:bg-[#551887] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-all text-sm sm:text-base"
              >
                <span>Continue to Location &amp; Details</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* STEP 2: Location, Email, Gender, DOB & Referral */}
          {currentStep === 1 && (
            <motion.div
              key="step-details"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="text-center space-y-1">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Personal &amp; Location Details
                </h2>
                <p className="text-sm text-gray-500 font-medium">
                  Final details to activate your partner profile
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-150 space-y-5">
                {/* Location with Google Places */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Primary Location / City *
                  </label>
                  <PlacesAutocomplete
                    value={locationAddress}
                    onChange={setLocationAddress}
                    onSelect={handleSelectLocation}
                    searchOptions={{
                      componentRestrictions: { country: "in" },
                    }}
                  >
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                      <div className="relative">
                        <MapPin className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                        <input
                          {...getInputProps({
                            placeholder: "Search or enter city (e.g. Mumbai, Bengaluru)",
                            className:
                              "w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors",
                          })}
                        />
                        {suggestions.length > 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto divide-y divide-gray-50">
                            {loading && (
                              <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Searching locations...</span>
                              </div>
                            )}
                            {suggestions.map((suggestion) => {
                              const active = suggestion.active;
                              return (
                                <div
                                  key={suggestion.placeId}
                                  {...getSuggestionItemProps(suggestion, {
                                    className: `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-3 transition-colors ${
                                      active
                                        ? "bg-purple-50 text-[#6720A4] font-medium"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`,
                                  })}
                                >
                                  <div
                                    className={`p-1.5 rounded-lg shrink-0 ${
                                      active ? "bg-purple-100 text-[#6720A4]" : "bg-gray-100 text-gray-400"
                                    }`}
                                  >
                                    <MapPin className="w-3.5 h-3.5" />
                                  </div>
                                  <span className="truncate">{suggestion.description}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="partner@tringbox.com"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors"
                    />
                  </div>
                </div>

                {/* Gender Pills */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Gender *
                  </label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {["Male", "Female", "Other"].map((g) => {
                      const isSelected = gender === g;
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`py-2 px-3 rounded-xl border text-xs sm:text-sm font-bold transition-all ${
                            isSelected
                              ? "border-[#6720A4] bg-purple-50 text-[#6720A4] shadow-2xs"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Date of Birth Selectors */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Date of Birth *
                  </label>
                  <div className="grid grid-cols-3 gap-2.5">
                    <select
                      value={dob.day}
                      onChange={(e) => setDob((prev) => ({ ...prev, day: e.target.value }))}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] bg-white cursor-pointer"
                    >
                      {days.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>

                    <select
                      value={dob.month}
                      onChange={(e) => setDob((prev) => ({ ...prev, month: e.target.value }))}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] bg-white cursor-pointer"
                    >
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>

                    <select
                      value={dob.year}
                      onChange={(e) => setDob((prev) => ({ ...prev, year: e.target.value }))}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] bg-white cursor-pointer"
                    >
                      {years.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Referral Code (Optional) */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Referral Code <span className="text-gray-400 font-normal lowercase">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    placeholder="Enter referral code if any"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors uppercase tracking-wider font-mono text-xs"
                  />
                </div>
              </div>

              {/* Complete & Go to Dashboard */}
              <button
                type="button"
                onClick={handleCompleteRegistration}
                disabled={isSubmitting}
                className="w-full bg-[#6720A4] hover:bg-[#551887] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-all disabled:opacity-60 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Activating Business Partner Account...</span>
                  </>
                ) : (
                  <>
                    <span>Complete &amp; Go to Business Dashboard</span>
                    <Sparkles className="w-4 h-4" />
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
